import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import { assertNever, isDefined } from '@freelancer/utils';
import { Store } from '@ngrx/store';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  BackendDeleteErrorTypes,
  BackendDeletePayloadTypes,
  BackendDeleteReturnTypes,
  BackendFetchPayloadTypes,
  BackendFetchReturnTypes,
  BackendPushErrorTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  BackendSetErrorTypes,
  BackendSetPayloadTypes,
  BackendSetReturnTypes,
  BackendUpdateErrorTypes,
  BackendUpdatePayloadTypes,
  BackendUpdateReturnTypes,
} from '../backend.model';
import { Collections, LegacySearchCollections } from '../collections.model';
import {
  CollectionActions,
  DeleteRequestPayload,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { ApiFetchResponse, ApiHttp } from './api-http.service';
import { ResponseData } from './datastore.interface';
import { RecursivePartial } from './helpers';
import { DatastoreMissingModuleError } from './missing-module-error';
import { Ordering, SingleOrdering } from './query';
import { pluckDocumentFromRawStore } from './store.helpers';
import {
  Path,
  RawQuery,
  Reference,
  SearchQueryParams,
  StoreState,
} from './store.model';

export interface BackendSuccessResponse {
  readonly status: 'success';
}

type ExtractId<T> = T extends { readonly id: any } ? T['id'] : undefined;

export interface BackendPushSuccessResponse<K extends keyof Collections>
  extends BackendSuccessResponse {
  readonly id: ExtractId<BackendPushReturnTypes[K]>;
}

export type BackendAllErrorCodes = ErrorCodeApi | 'UNKNOWN_ERROR';

export interface BackendErrorResponse<E> {
  readonly status: 'error';
  readonly errorCode: E;
  readonly requestId?: string;
}

export type BackendPushErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendPushErrorTypes[K]>;
export type BackendSetErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendSetErrorTypes[K]>;
export type BackendUpdateErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendUpdateErrorTypes[K]>;
export type BackendDeleteErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendDeleteErrorTypes[K]>;

export type BackendPushResponse<K extends keyof Collections> =
  | BackendPushSuccessResponse<K>
  | BackendPushErrorResponse<K>;
export type BackendSetResponse<K extends keyof Collections> =
  | BackendSuccessResponse
  | BackendSetErrorResponse<K>;
export type BackendUpdateResponse<K extends keyof Collections> =
  | BackendSuccessResponse
  | BackendUpdateErrorResponse<K>;
export type BackendDeleteResponse<K extends keyof Collections> =
  | BackendSuccessResponse
  | BackendDeleteErrorResponse<K>;

/** Key-value pairs passed to the backend via query parameters in the URL */
export interface Params {
  readonly [key: string]:
    | string
    | number
    | boolean
    | ReadonlyArray<number | undefined>
    | ReadonlyArray<string | undefined>
    | undefined;
}

interface BackendFetchGetRequest {
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
}

interface BackendFetchPostRequest<
  K extends keyof (Collections & LegacySearchCollections)
> {
  readonly payload: BackendFetchPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
  readonly method: 'POST';
}

export type BackendFetchRequest<
  K extends keyof (Collections & LegacySearchCollections)
> = BackendFetchGetRequest | BackendFetchPostRequest<K>;

type FetchRequestFactory<K extends keyof Collections> = (
  authUid: string,
  ids: ReadonlyArray<string> | undefined,
  query: RawQuery<Collections[K]> | undefined,
  order: Ordering<K> | undefined,
) => BackendFetchRequest<K>;

export interface BackendPushRequest<K extends keyof Collections> {
  readonly payload: BackendPushPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
}

type PushRequestFactory<K extends keyof Collections> = (
  authUid: string,
  document: Collections[K],
  extra: { readonly [index: string]: string | number } | undefined,
) => BackendPushRequest<K>;

export interface BackendSetRequest<K extends keyof Collections> {
  readonly payload: BackendSetPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
}

type SetRequestFactory<K extends keyof Collections> = (
  authUid: string,
  document: Collections[K],
) => BackendSetRequest<K>;

export interface BackendUpdateRequest<K extends keyof Collections> {
  readonly payload: BackendUpdatePayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT';
}

type UpdateRequestFactory<K extends keyof Collections> = (
  authUid: string,
  delta: RecursivePartial<Collections[K]>,
  originalDocument: Collections[K],
) => BackendUpdateRequest<K>;

export interface BackendDeleteRequest<K extends keyof Collections> {
  readonly payload: BackendDeletePayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT' | 'DELETE';
}

type DeleteRequestFactory<K extends keyof Collections> = (
  authUid: string,
  id: string | number,
  originalDocument: Collections[K],
) => BackendDeleteRequest<K>;

export interface Backend<K extends keyof Collections> {
  readonly fetch?: FetchRequestFactory<K>;
  readonly push?: PushRequestFactory<K>;
  readonly set?: SetRequestFactory<K>;
  readonly update?: UpdateRequestFactory<K>;
  readonly remove?: DeleteRequestFactory<K>;
  readonly defaultOrder: SingleOrdering<K> | Ordering<K>;
  readonly maxBatchSize?: number;
}

export const BACKEND_DEFAULT_BATCH_SIZE = 100;

export interface BackendSearch<K extends keyof LegacySearchCollections> {
  search(query: SearchQueryParams): BackendFetchRequest<K>;
}

export type BackendConfigs = { [K in keyof Collections]?: Backend<K> } &
  { [K in keyof LegacySearchCollections]?: BackendSearch<K> };

@Injectable()
export class StoreBackend {
  private backendConfigs: BackendConfigs = {};

  constructor(private store$: Store<StoreState>, private apiHttp: ApiHttp) {}

  /**
   * Checks if the backend config (*.backend.ts) for a given collection is available.
   * It is only available if the BackendFeatureModule (or DatastoreFeatureModule)
   * for that collection has been imported.
   *
   * Note that this does not check if the @ngrx/store feature module for that
   * collection has been imported. This is only possible when we have access to
   * store state, i.e. upon subscription to the store. However, since the both
   * the store and backend feature modules are imported together in the various
   * `DatastoreXModule` modules, checking one of these is equivalent to checking
   * the other.
   */
  isFeatureLoaded<K extends keyof Collections>(collection: K): boolean {
    return collection in this.backendConfigs;
  }

  defaultOrder<K extends keyof Collections>(collection: K): Ordering<K> {
    const config = this.backendConfigs[collection] as Backend<K> | undefined;
    if (!config) {
      throw new DatastoreMissingModuleError(collection);
    }
    return Array.isArray(config.defaultOrder)
      ? config.defaultOrder
      : ([config.defaultOrder] as Ordering<K>);
  }

  batchSize<K extends keyof Collections>(ref: Reference<K>): number {
    const config = this.backendConfigs[ref.path.collection];
    return (config && config.maxBatchSize) || BACKEND_DEFAULT_BATCH_SIZE;
  }

  fetch<K extends keyof Collections>(
    ref: Reference<K>,
  ): Rx.Observable<ApiFetchResponse<K>> {
    const { path, query, order } = ref;
    const type = path.collection;
    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.fetch;
      }),
      filter(isDefined),
      map(fetch => fetch(path.authUid, path.ids, query, order)),
      switchMap(request => this.apiHttp.fetch(request, query)),
    );
  }

  search<K extends keyof LegacySearchCollections>(
    collection: K,
    query$: Rx.Observable<SearchQueryParams>,
  ): Rx.Observable<BackendFetchReturnTypes[K]> {
    return query$.pipe(
      map(q => {
        const config = this.backendConfigs[collection];
        if (!config) {
          throw new DatastoreMissingModuleError(collection);
        }
        return config.search(q);
      }),
      switchMap(request => this.apiHttp.fetch(request)),
      map(
        response =>
          (response.status === 'success'
            ? response.result
            : []) as BackendFetchReturnTypes[K],
      ), // FIXME: Error handling?
    );
  }

  push<K extends keyof Collections>(
    ref: Reference<K>,
    document: Collections[K],
    extra?: { readonly [index: string]: string | number },
  ): Rx.Observable<BackendPushResponse<K>> {
    const { path, query } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.push;
      }),
      filter(isDefined),
      map(push => {
        const request = push(path.authUid, document, extra);
        return {
          request,
          payload: {
            type,
            ref,
            document,
            rawRequest: request.payload,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_PUSH`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) =>
        this.apiHttp.post(request).pipe(map(result => ({ payload, result }))),
      ),
      map(({ payload, result }) =>
        this.sendActions({ type: 'PUSH', payload }, path, query, result),
      ),
    );
  }

  set<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    document: Collections[K],
  ): Rx.Observable<BackendSetResponse<K>> {
    const { path, query } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.set;
      }),
      filter(isDefined),
      withLatestFrom(this.store$),
      map(([set, storeState]) => {
        const originalDocument = pluckDocumentFromRawStore(
          storeState,
          path,
          id,
        );
        if (originalDocument === undefined) {
          throw new Error('Missing original document');
        }
        const request = set(path.authUid, document);
        return {
          request,
          payload: {
            type,
            ref,
            document,
            originalDocument,
            rawRequest: request.payload,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_SET`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) =>
        this.apiHttp.post(request).pipe(map(result => ({ payload, result }))),
      ),
      map(({ payload, result }) =>
        this.sendActions({ type: 'SET', payload }, path, query, result),
      ),
    );
  }

  update<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    delta: RecursivePartial<Collections[K]>,
  ): Rx.Observable<BackendUpdateResponse<K>> {
    const { path } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.update;
      }),
      filter(isDefined),
      withLatestFrom(this.store$),
      map(([update, storeState]) => {
        const originalDocument = pluckDocumentFromRawStore(
          storeState,
          path,
          id,
        );
        if (originalDocument === undefined) {
          throw new Error('Missing original document');
        }
        const request = update(path.authUid, delta, originalDocument);
        return {
          request,
          payload: {
            type,
            ref,
            delta,
            rawRequest: request.payload,
            originalDocument,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_UPDATE`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) => {
        switch (request.method) {
          case 'POST':
            return this.apiHttp
              .post(request)
              .pipe(map(result => ({ payload, result })));
          case 'PUT':
            return this.apiHttp
              .put(request)
              .pipe(map(result => ({ payload, result })));
          default:
            return assertNever(request.method);
        }
      }),
      map(({ payload, result }) =>
        this.sendActions(
          { type: 'UPDATE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  delete<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
  ): Rx.Observable<BackendDeleteResponse<K>> {
    const { path } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.remove;
      }),
      filter(isDefined),
      withLatestFrom(this.store$),
      map(([del, storeState]) => {
        const originalDocument = pluckDocumentFromRawStore(
          storeState,
          path,
          id,
        );
        if (originalDocument === undefined) {
          throw new Error('Missing original document');
        }
        const request = del(path.authUid, id, originalDocument);
        return {
          request,
          payload: {
            type,
            ref,
            rawRequest: request.payload,
            originalDocument,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_DELETE`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) => {
        switch (request.method) {
          case 'POST':
            return this.apiHttp
              .post(request)
              .pipe(map(result => ({ payload, result })));
          case 'PUT':
            return this.apiHttp
              .put(request)
              .pipe(map(result => ({ payload, result })));
          case 'DELETE':
            return this.apiHttp
              .delete(request)
              .pipe(map(result => ({ payload, result })));
          default:
            return assertNever(request.method);
        }
      }),
      map(({ payload, result }) =>
        this.sendActions(
          { type: 'DELETE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'PUSH';
      readonly payload: PushRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendPushReturnTypes[K], BackendPushErrorTypes[K]>,
  ): BackendPushResponse<K>;
  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'SET';
      readonly payload: SetRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendSetReturnTypes[K], BackendSetErrorTypes[K]>,
  ): BackendSetResponse<K>;
  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'UPDATE';
      readonly payload: UpdateRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>,
  ): BackendUpdateResponse<K>;
  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'DELETE';
      readonly payload: DeleteRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>,
  ): ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>;
  private sendActions<K extends keyof Collections>(
    baseAction:
      | { readonly type: 'PUSH'; readonly payload: PushRequestPayload<K> }
      | { readonly type: 'SET'; readonly payload: SetRequestPayload<K> }
      | { readonly type: 'UPDATE'; readonly payload: UpdateRequestPayload<K> }
      | { readonly type: 'DELETE'; readonly payload: DeleteRequestPayload<K> },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data:
      | ResponseData<BackendPushReturnTypes[K], BackendPushErrorTypes[K]>
      | ResponseData<BackendSetReturnTypes[K], BackendSetErrorTypes[K]>
      | ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>
      | ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>,
  ):
    | BackendPushResponse<K>
    | BackendSetResponse<K>
    | BackendUpdateResponse<K>
    | BackendDeleteResponse<K> {
    switch (data.status) {
      case 'success': {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? ({
                type: 'API_PUSH_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  document: baseAction.payload.document,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>)
            : baseAction.type === 'SET'
            ? ({
                type: 'API_SET_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  document: baseAction.payload.document,
                  originalDocument: baseAction.payload.originalDocument,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>)
            : baseAction.type === 'UPDATE'
            ? ({
                type: 'API_UPDATE_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  delta: baseAction.payload.delta,
                  originalDocument: baseAction.payload.originalDocument,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>)
            : ({
                type: 'API_DELETE_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  originalDocument: baseAction.payload.originalDocument,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>);
        this.store$.dispatch(action);
        return baseAction.type === 'PUSH'
          ? ({
              status: 'success',
              id: data.result && data.result.id,
            } as BackendSuccessResponse)
          : { status: 'success' };
      }
      default: {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_ERROR',
                payload: baseAction.payload,
              }
            : baseAction.type === 'SET'
            ? {
                type: 'API_SET_ERROR',
                payload: baseAction.payload,
              }
            : baseAction.type === 'UPDATE'
            ? {
                type: 'API_UPDATE_ERROR',
                payload: baseAction.payload,
              }
            : {
                type: 'API_DELETE_ERROR',
                payload: baseAction.payload,
              };
        this.store$.dispatch(action);
        return data;
      }
    }
  }

  addFeature<K extends keyof Collections>(
    collectionName: K,
    requestFactory: Backend<K>,
  ): void;

  addFeature<K extends keyof LegacySearchCollections>(
    collectionName: K,
    requestFactory: BackendSearch<K>,
  ): void;

  addFeature<K extends keyof (Collections & LegacySearchCollections)>(
    collectionName: K,
    requestFactory: any,
  ): void {
    this.backendConfigs[collectionName] = requestFactory;
  }
}

export const BACKEND_COLLECTIONS = new InjectionToken<ReadonlyArray<string>>(
  'Backend Collections',
);

export const BACKEND_CONFIGS = new InjectionToken<SearchConfigsProvider>(
  'Backend Configs',
);

type BackendConfigFactory<K extends keyof Collections> = () => Backend<K>;

type BackendSearchConfigFactory<
  K extends keyof LegacySearchCollections
> = () => BackendSearch<K>;

export type SearchConfigsProvider = ReadonlyArray<Backend<keyof Collections>> &
  ReadonlyArray<BackendSearch<keyof LegacySearchCollections>>;

@NgModule({})
export class BackendRootModule {}

@NgModule({})
export class BackendFeatureModule<
  K extends keyof (Collections & LegacySearchCollections)
> {
  constructor(
    storeBackend: StoreBackend,
    @Inject(BACKEND_COLLECTIONS) backendCollections: ReadonlyArray<K>,
    @Inject(BACKEND_CONFIGS) backendConfigs: SearchConfigsProvider,
  ) {
    backendCollections.map((collectionName, index) => {
      storeBackend.addFeature(collectionName as any, backendConfigs[index]); // FIXME: any type
    });
  }
}

@NgModule({})
export class BackendModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: BackendRootModule,
      providers: [StoreBackend],
    };
  }

  static forFeature<K extends keyof Collections>(
    collectionName: K,
    configFactory: BackendConfigFactory<K>,
  ): ModuleWithProviders;

  static forFeature<K extends keyof LegacySearchCollections>(
    collectionName: K,
    configFactory: BackendSearchConfigFactory<K>,
  ): ModuleWithProviders;

  static forFeature<K extends keyof (Collections & LegacySearchCollections)>(
    collectionName: K,
    configFactory: any,
  ): ModuleWithProviders {
    return {
      ngModule: BackendFeatureModule,
      providers: [
        {
          provide: BACKEND_COLLECTIONS,
          multi: true,
          useValue: collectionName,
        },
        {
          provide: BACKEND_CONFIGS,
          multi: true,
          useFactory: configFactory,
        },
      ],
    };
  }
}
